import React, { useState } from 'react';
import { useOptions } from '../HRContext';
import { errorText } from '../../utils';
import { titleCase } from '../format';
import Modal from './Modal';
import { FileSlot, openStored } from './Files';
import { DateInput, MonthInput, Select } from './Controls';

// Field definition: { name, label, type, required, options, ref, placeholder, min, max, full, help }
// type: text (default) | textarea | select | ref | boolean | date | time | number | integer | money | email | file
// file: { rule: data => rule (src/hr/fileRules.js), stored: record => file info, open: record => Blob promise,
//         upload: (record, file) => promise } — the picked File is uploaded after the record is saved (Register)

const optionPairs = options => (options || []).map(o => (typeof o === 'object' ? o : { value: o, label: o ? titleCase(o) : '(none)' }));

function RefSelect({ field, value, onChange, disabled }) {
  const options = useOptions(field.ref);
  const shown = field.filterOptions ? options.filter(field.filterOptions) : options;
  return (
    <Select title={field.label} value={value ?? ''} onChange={e => onChange(e.target.value)} required={field.required} disabled={disabled}>
      <option value="">{field.placeholder || '— Select —'}</option>
      {shown.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </Select>
  );
}

export function Input({ field, value, onChange, disabled, data, record }) {
  switch (field.type) {
    case 'file': {
      const stored = record && field.stored ? field.stored(record) : null;
      return <FileSlot rule={field.rule(data || {})} stored={stored} pending={value || null} onPick={onChange} onClear={() => onChange(null)}
                       onOpen={stored && field.open ? () => openStored(() => field.open(record)) : undefined} />;
    }
    case 'ref':
      return <RefSelect field={field} value={value} onChange={onChange} disabled={disabled} />;
    case 'select':
      return (
        <Select title={field.label} value={value ?? ''} onChange={e => onChange(e.target.value)} required={field.required} disabled={disabled}>
          {!field.noBlank && <option value="">{field.placeholder || '— Select —'}</option>}
          {optionPairs(field.options).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Select>
      );
    case 'boolean':
      return <label className="switch"><input type="checkbox" checked={!!value} disabled={disabled} onChange={e => onChange(e.target.checked)} /> {value ? 'Yes' : 'No'}</label>;
    case 'textarea':
      return <textarea rows={3} value={value ?? ''} placeholder={field.placeholder} disabled={disabled} onChange={e => onChange(e.target.value)} required={field.required} />;
    case 'month':
      return <MonthInput value={value ? String(value).slice(0, 7) : ''} disabled={disabled} onChange={e => onChange(e.target.value)} required={field.required} />;
    case 'date':
      return <DateInput value={value ? String(value).slice(0, 10) : ''} min={field.min} max={field.max} disabled={disabled} onChange={e => onChange(e.target.value)} required={field.required} />;
    case 'time':
      return <input type="time" value={value ?? ''} disabled={disabled} onChange={e => onChange(e.target.value)} required={field.required} />;
    case 'integer':
    case 'number':
    case 'money':
      return <input type="number" step={field.type === 'integer' ? 1 : 'any'} min={field.min ?? (field.type === 'money' ? 0 : undefined)} max={field.max} value={value ?? ''} placeholder={field.placeholder} disabled={disabled} onChange={e => onChange(e.target.value)} required={field.required} />;
    default:
      // suggestions: pick from a list or type a new value
      return (
        <>
          <input type={field.type === 'email' ? 'email' : 'text'} value={value ?? ''} placeholder={field.placeholder} list={field.suggestions ? `dl-${field.name}` : undefined}
                 maxLength={field.maxLength} disabled={disabled} onChange={e => onChange(e.target.value)} required={field.required} />
          {field.suggestions && <datalist id={`dl-${field.name}`}>{field.suggestions.map(s => <option key={s} value={s} />)}</datalist>}
        </>
      );
  }
}

export function Field({ field, value, onChange, disabled, data, record }) {
  return (
    <div className={`field ${field.full || field.type === 'textarea' || field.type === 'file' ? 'full' : ''}`}>
      <span className="field-label">{field.label}{field.required && <em>*</em>}</span>
      <Input field={field} value={value} onChange={onChange} disabled={disabled} data={data} record={record} />
      {field.help && <small className="muted small">{field.help}</small>}
    </div>
  );
}

// after a save: upload each file the form picked (fields of type 'file' holding a File)
export async function uploadPickedFiles(fields, data, record) {
  for (const f of fields.filter(x => x.type === 'file' && data[x.name] instanceof File)) {
    await f.upload(record, data[f.name]);
  }
}

// form values -> API payload: blanks become null, numbers become numbers
export function toPayload(fields, data) {
  const out = {};
  fields.forEach(f => {
    let v = data[f.name];
    if (v === undefined || f.type === 'file') return; // files go up separately, after the record is saved
    if (v === '') v = null;
    else if (['number', 'integer', 'money'].includes(f.type) && v !== null) v = Number(v);
    out[f.name] = v;
  });
  return out;
}

export default function RecordForm({ title, fields, initial, onClose, onSubmit, submitLabel = 'Save', wide }) {
  const [data, setData] = useState(() => {
    const d = { ...(initial || {}) };
    fields.forEach(f => { if (d[f.name] === undefined && f.default !== undefined) d[f.name] = typeof f.default === 'function' ? f.default() : f.default; });
    return d;
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async e => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await onSubmit(toPayload(fields, data), data);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  return (
    <Modal title={title} onClose={onClose} wide={wide ?? fields.length > 8}>
      <form onSubmit={save} className="form-grid">
        {fields.map(f => (
          <Field key={f.name} field={f} value={f.type === 'file' ? (data[f.name] instanceof File ? data[f.name] : null) : data[f.name]}
                 onChange={v => setData(d => ({ ...d, [f.name]: v }))} data={data} record={initial} />
        ))}
        {error && <div className="error full">{error}</div>}
        <div className="form-actions full">
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={busy}>{busy ? 'Saving…' : submitLabel}</button>
        </div>
      </form>
    </Modal>
  );
}
